<template>
  <div style="width: 100%; height: 80vh; min-height: 600px">
    <div ref="gmap" style="width: 100%; height: 100%"></div>
    <gmap-cluster>
      <gmap-marker
        v-for="m in markers"
        :key="m.key"
        :geoCoord="m.geoCoord"
        :title="m.title"
        :subtitle="m.subtitle"
        :mdi="m.mdi"
        :icon="m.icon"
        :id="m.id"
        :geoCoordId="m.geoCoordId"
        :coordinates="m.coordinates"
      >
      </gmap-marker>
    </gmap-cluster>
  </div>
</template>

<script>
import GmapMarker from '@/components/GmapMarker.vue'
import GmapCluster from '@/components/GmapCluster.vue'
// import GmapInfoWindow from '@/components/GmapInfoWindow.vue'
import { Iri } from '@/rec-cache/rec-cache-types'
import { recIcon } from '@/vue-wob-cache/vue-wob-icons'

import humanizeString from 'humanize-string'

const recCoordinates = 'https://w3id.org/rec#coordinates'

// TODO go over markers and cluster classes re https://github.com/googlemaps/js-markerclusterer/issues/508

// Utility to aggregate all relevant information for marker.
class Marker {
  constructor(geoCoord, title, type, id, geoCoordId, coordinates) {
    this.geoCoord = geoCoord
    this.title = title
    this.subtitle = humanizeString(type.short)
    this.mdi = recIcon(type)
    this.id = id
    this.geoCoordId = geoCoordId
    this.coordinates = coordinates
    this.showInfoWindow = false

    const icon = document.createElement('div')
    icon.innerHTML = `<i class="mdi ${this.mdi} mdi-18px"></i>`

    this.icon = icon

    // The unique key is formed with a hash over the geo coordinate plus the ID as there seem to be duplicate geo coords.
    // TODO it may be faster to use a small and fast hash on the whole marker object.
    // Also consider if there can be several of the exact same geo coordinates on the same individual in which case something more is needed.
    const buffer = new ArrayBuffer(16)
    const view = new Float64Array(buffer)
    view[0] = geoCoord.latitude
    view[1] = geoCoord.longitude
    this.key =
      [...new Uint8Array(buffer)]
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('') + id.short
  }
}

// TODO move map id into config somewhere.
export default {
  components: { GmapMarker, GmapCluster },
  data: () => ({
    mapOptions: {
      center: {
        lat: 59,
        lng: 15,
      },
      zoom: 6,
      // Use custom mapId for cloud-based styling (current approach)
      mapId: '130b0aba980e9629',
      tilt: 45,
      mapTypeControl: false, // Using custom control instead
      // OR uncomment below for a custom styled map (remove mapId above if using this)
      // styles: [
      //   {
      //     featureType: 'all',
      //     elementType: 'geometry',
      //     stylers: [{ color: '#242f3e' }],
      //   },
      //   {
      //     featureType: 'all',
      //     elementType: 'labels.text.stroke',
      //     stylers: [{ color: '#242f3e' }],
      //   },
      //   {
      //     featureType: 'all',
      //     elementType: 'labels.text.fill',
      //     stylers: [{ color: '#746855' }],
      //   },
      //   {
      //     featureType: 'water',
      //     elementType: 'geometry',
      //     stylers: [{ color: '#17263c' }],
      //   },
      //   {
      //     featureType: 'road',
      //     elementType: 'geometry',
      //     stylers: [{ color: '#38414e' }],
      //   },
      //   {
      //     featureType: 'road',
      //     elementType: 'geometry.stroke',
      //     stylers: [{ color: '#212a37' }],
      //   },
      //   {
      //     featureType: 'road',
      //     elementType: 'labels.text.fill',
      //     stylers: [{ color: '#9ca5b3' }],
      //   },
      //   {
      //     featureType: 'poi',
      //     elementType: 'geometry',
      //     stylers: [{ color: '#283d6a' }],
      //   },
      //   {
      //     featureType: 'landscape',
      //     elementType: 'geometry',
      //     stylers: [{ color: '#2c3e50' }],
      //   },
      // ],
    },
    markerOptions: {
      position: { lat: 59, lng: 18 },
      map: null,
    },
    advMarkerViewOptions: {
      position: { lat: 59, lng: 18 },
      map: null,
      content: null,
      title: 'Anonymous Building',
    },
    map: null,
    mapPromiseResolveFunc: null,
    gmapMarkers: [],
    keys: [],
    showInfoWindows: [],
  }),
  computed: {
    markers: function () {
      return this.$wob.geoCoords.ids
        .map((id) => this.$wob.read(new Iri(id), true))
        .map((i) =>
          [
            // The individual itself replaced with array of markers.
            i.meta.geoCoords.list.map(
              (g) =>
                new Marker(
                  g,
                  i.label.value,
                  i.type,
                  i.id,
                  i.id,
                  i.getValuesByName(recCoordinates)?.[0]
                )
            ),
            // Each components replaced with array of markers.
            ...i.components.map((c) =>
              c.meta.geoCoords.list.map(
                (g) =>
                  new Marker(
                    g,
                    i.label.value,
                    i.type,
                    i.id,
                    c.id,
                    c.getValuesByName(recCoordinates)?.[0]
                  )
              )
            ),
          ].filter((m) => !!m && m.length)
        )
        .reduce((prev, curr) => prev.concat(curr), [])
        .reduce((prev, curr) => prev.concat(curr), [])
    },
    s: function () {
      return this.markers.length
        ? this.markers.reduce((prev, curr) =>
            curr.geoCoord.latitude < prev.geoCoord.latitude ? curr : prev
          ).geoCoord.latitude
        : 57
    },
    w: function () {
      return this.markers.length
        ? this.markers.reduce((prev, curr) =>
            curr.geoCoord.longitude < prev.geoCoord.longitude ? curr : prev
          ).geoCoord.longitude
        : 13
    },
    n: function () {
      return this.markers.length
        ? this.markers.reduce((prev, curr) =>
            curr.geoCoord.latitude > prev.geoCoord.latitude ? curr : prev
          ).geoCoord.latitude
        : 61
    },
    e: function () {
      return this.markers.length
        ? this.markers.reduce((prev, curr) =>
            curr.geoCoord.longitude > prev.geoCoord.longitude ? curr : prev
          ).geoCoord.longitude
        : 17
    },
  },
  provide: function () {
    // console.log('views.gmaps-view.provide')
    const mapPromise = new Promise((resolve) => {
      this.mapPromiseResolveFunc = resolve
    })
    return { mapPromise: mapPromise }
  },
  created: function () {
    // console.log('views.gmap-view.created')
  },
  mounted: async function () {
    await this.$gmapReady
    // Get zoom and center from store.
    const zoom = this.$store.getters.getZoom
    const center = this.$store.getters.getCenter
    if (zoom) this.mapOptions.zoom = zoom
    if (center) this.mapOptions.center = center

    this.map = new this.$gmap.maps.Map(this.$refs.gmap, this.mapOptions)
    this.mapPromiseResolveFunc(this.map)

    // Create the DIV to hold the bottom-left map controls.
    const bottomControlsDiv = document.createElement('div')
    bottomControlsDiv.style.display = 'flex'
    bottomControlsDiv.style.margin = '10px'

    // Create the "Markers" control (fit to all markers).
    const markersControl = this.createFitControl(this.map)
    bottomControlsDiv.appendChild(markersControl)

    // Create the "Reset View" control (zoom to Scandinavia).
    const resetControl = this.createResetControl(this.map)
    bottomControlsDiv.appendChild(resetControl)

    this.map.controls[this.$gmap.maps.ControlPosition.LEFT_BOTTOM].push(
      bottomControlsDiv
    )

    // if (!zoom && !center) this.fitMap()
  },
  // TODO methods no longer in use...
  methods: {
    // Parameter id is an Iri.
    gmapLatLng(id) {
      // TODO make work with several coordinates per individual
      const [geoCoordinate] = this.$wob.readGeoCoords(id, true)
      // console.log(
      //   'views.gmap-view.methods.gmap-lat-lng',
      //   id.value,
      //   geoCoordinate
      // )
      return new this.$gmap.maps.LatLng(
        geoCoordinate.latitude,
        geoCoordinate.longitude
      )
    },
    // Parameter id is an Iri.
    mdi(type) {
      return recIcon(type)
    },
    createMarker(map, position, title, mdi) {
      const icon = document.createElement('div')
      icon.innerHTML = `<i class="mdi ${mdi} mdi-18px"></i>`

      // Create a custom marker element with the MDI icon
      const markerContent = document.createElement('div')
      markerContent.style.cssText = `
        background: #AFB42B;
        border: 2px solid white;
        border-radius: 50% 50% 50% 0;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        transform: rotate(-45deg);
        position: relative;
      `

      // Add the icon container (counter-rotate the icon so it appears upright)
      const iconContainer = document.createElement('div')
      iconContainer.style.cssText = 'transform: rotate(45deg);'
      iconContainer.appendChild(icon)
      markerContent.appendChild(iconContainer)

      const marker = new this.$gmap.maps.marker.AdvancedMarkerElement({
        map: map,
        position: position,
        title: title,
        content: markerContent,
      })

      marker.addListener('click', this.onClick)
    },
    onClick(e) {
      console.log('views.gmap-view.methods.on-click', e)
      // console.log(this.map.getCenter().toJSON(), this.map.getZoom())
    },
    // fitMap: function () {
    //   const sw = new this.$gmap.maps.LatLng(this.s, this.w)
    //   const ne = new this.$gmap.maps.LatLng(this.s, this.w)
    //   this.map?.fitBounds({ sw, ne })
    // },
    createFitControl: function (map) {
      const controlButton = document.createElement('button')

      // Set CSS for the control (left button in group) - Material Design style.
      controlButton.style.backgroundColor = '#fff'
      controlButton.style.border = 'none'
      controlButton.style.borderRadius = '2px 0 0 2px'
      controlButton.style.borderRight = '1px solid #dadce0'
      controlButton.style.boxShadow = 'rgba(0,0,0,.3) 0px 1px 4px -1px'
      controlButton.style.color = 'rgb(25, 25, 25)'
      controlButton.style.cursor = 'pointer'
      controlButton.style.fontFamily = 'Roboto,Arial,sans-serif'
      controlButton.style.fontSize = '14px'
      controlButton.style.fontWeight = '500'
      controlButton.style.letterSpacing = '0.0892857143em'
      controlButton.style.lineHeight = '38px'
      controlButton.style.margin = '0'
      controlButton.style.padding = '0 17px'
      controlButton.style.textAlign = 'center'
      controlButton.style.textTransform = 'uppercase'

      controlButton.textContent = 'Markers'
      controlButton.title = 'Show all markers'
      controlButton.type = 'button'

      // Setup the click event listeners.
      // TODO store an then remove listener at destory...
      controlButton.addEventListener('click', () => {
        const sw = new this.$gmap.maps.LatLng(this.s, this.w)
        const ne = new this.$gmap.maps.LatLng(this.n, this.e)

        map?.fitBounds(new this.$gmap.maps.LatLngBounds(sw, ne), 40)
      })

      return controlButton
    },
    createResetControl: function (map) {
      const controlButton = document.createElement('button')

      // Set CSS for the control (right button in group) - Material Design style.
      controlButton.style.backgroundColor = '#fff'
      controlButton.style.border = 'none'
      controlButton.style.borderRadius = '0 2px 2px 0'
      controlButton.style.boxShadow = 'rgba(0,0,0,.3) 0px 1px 4px -1px'
      controlButton.style.color = 'rgb(25, 25, 25)'
      controlButton.style.cursor = 'pointer'
      controlButton.style.fontFamily = 'Roboto,Arial,sans-serif'
      controlButton.style.fontSize = '14px'
      controlButton.style.fontWeight = '500'
      controlButton.style.letterSpacing = '0.0892857143em'
      controlButton.style.lineHeight = '38px'
      controlButton.style.margin = '0'
      controlButton.style.padding = '0 17px'
      controlButton.style.textAlign = 'center'
      controlButton.style.textTransform = 'uppercase'

      controlButton.textContent = 'Reset View'
      controlButton.title = 'Reset to Scandinavia'
      controlButton.type = 'button'

      // Setup the click event listener to reset to default Scandinavia view.
      controlButton.addEventListener('click', () => {
        map?.setCenter({ lat: 59, lng: 15 })
        map?.setZoom(6)
      })

      return controlButton
    },
  },
  beforeRouteLeave: function (to, from, next) {
    console.log('views.map-view.before-route-leave')
    // TODO save maps zoom and position
    if (this.map) {
      this.$store.commit('setZoom', this.map.getZoom())
      this.$store.commit('setCenter', this.map.getCenter())
    }
    next()
  },
}
</script>
